/**
 * Display rules for the multi-step CTA overview: what each row's status pill says, its one-line
 * summary, whether it opens, and which step "Continue" goes to. Wording follows the booking's real
 * state (a submitted booking is "Requested", not "Confirmed") so a guest scanning the pills isn't
 * told more than is true.
 */

import type { CtaStepOverview, CtaStepsOverview } from "./cta-steps";

/** Visual tone of a status pill. */
export type OverviewPillTone = "success" | "info" | "warning" | "danger" | "neutral";

/** How one overview row reads. */
export interface OverviewStepDisplay {
  pill: string;
  tone: OverviewPillTone;
  /** One-line summary under the step name, or null. */
  summary: string | null;
  /** Whether the row opens its step. */
  openable: boolean;
  /** An active booking held by a guest who declined: the row asks them to keep or release it. */
  stillBooked: boolean;
}

const SHORT_DATE = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const SHORT_DATE_UTC = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** "Sep 2" from an ISO timestamp, or null. */
function formatTimestamp(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : SHORT_DATE.format(date);
}

/** "Feb 22" from a YYYY-MM-DD date, read as a calendar date (no timezone shift). */
function formatCalendarDate(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? value : SHORT_DATE_UTC.format(date);
}

/**
 * Whether a step counts as done for choosing the overview over the step run.
 * @param step - One overview step
 * @returns true once the guest submitted (or declined) a form, or has any booking outcome
 * @throws Never
 */
export function isOverviewStepDone(step: CtaStepOverview): boolean {
  if (step.kind === "form") return step.status === "submitted" || step.status === "declined";
  return step.status !== "not-started" && step.status !== "unavailable";
}

/**
 * How an overview row reads.
 * @param step - The step
 * @param hasDeclined - Whether the guest declined in an earlier form step (e.g. RSVP "not attending")
 * @returns The row's pill, tone, summary, and whether it opens
 * @throws Never
 */
export function describeOverviewStep(step: CtaStepOverview, hasDeclined: boolean): OverviewStepDisplay {
  if (step.kind === "form") {
    const submitted = formatTimestamp(step.submittedAt);
    const summary = submitted ? `Submitted ${submitted}` : null;
    switch (step.status) {
      case "submitted":
        return { pill: "Submitted", tone: "success", summary, openable: true, stillBooked: false };
      case "declined":
        return { pill: "Not attending", tone: "neutral", summary, openable: true, stillBooked: false };
      case "unavailable":
        return { pill: "Unavailable", tone: "neutral", summary: null, openable: false, stillBooked: false };
      default:
        return { pill: "Not started", tone: "neutral", summary: null, openable: true, stillBooked: false };
    }
  }

  const summary =
    step.checkinDate && step.checkoutDate
      ? `${formatCalendarDate(step.checkinDate)} to ${formatCalendarDate(step.checkoutDate)}`
      : null;
  const isActive = step.status === "requested" || step.status === "confirmed";
  if (hasDeclined && isActive) {
    return { pill: "Still booked", tone: "warning", summary, openable: true, stillBooked: true };
  }

  switch (step.status) {
    case "requested":
      return { pill: "Requested", tone: "info", summary, openable: true, stillBooked: false };
    case "confirmed":
      return { pill: "Confirmed", tone: "success", summary, openable: true, stillBooked: false };
    case "not-approved":
      return { pill: "Not approved", tone: "danger", summary, openable: true, stillBooked: false };
    case "cancelled":
      return { pill: "Cancelled", tone: "neutral", summary, openable: true, stillBooked: false };
    case "unavailable":
      return { pill: "Unavailable", tone: "neutral", summary: null, openable: false, stillBooked: false };
    default:
      return hasDeclined
        ? { pill: "Not needed", tone: "neutral", summary: null, openable: false, stillBooked: false }
        : { pill: "Not started", tone: "neutral", summary: null, openable: true, stillBooked: false };
  }
}

/**
 * Whether the guest declined in a form step (its answers ended the form early, e.g. an RSVP of
 * "not attending"), which makes the later steps unnecessary.
 * @param overview - The overview
 * @returns true when any form step is declined
 * @throws Never
 */
export function hasDeclinedStep(overview: CtaStepsOverview): boolean {
  return overview.steps.some((step) => step.kind === "form" && step.status === "declined");
}

/**
 * The step "Continue" should open: the first one not started yet that can open. Nothing once the
 * guest declined, since the later steps aren't needed.
 * @param overview - The overview
 * @returns The step index, or null when there's nothing left to do
 * @throws Never
 */
export function findContinueIndex(overview: CtaStepsOverview): number | null {
  if (hasDeclinedStep(overview)) return null;
  const index = overview.steps.findIndex(
    (step) => step.status === "not-started" && describeOverviewStep(step, false).openable
  );
  return index === -1 ? null : index;
}
